import React, { useState } from 'react';
import { clinicService, analyticsService } from '../../services/api';

// Import icons
import { ReactComponent as DownloadIcon } from '../../assets/download.svg';
import ConfigJsonUpdatePanel from './ConfigJsonUpdatePanel';

const ConfigExportSection = ({ clinicId, onDataChange }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [isExportingReport, setIsExportingReport] = useState(false);
  const [reportDays, setReportDays] = useState(30);
  const [reportLocale, setReportLocale] = useState('zh-TW');
  const [reportError, setReportError] = useState(null);

  const handleExport = async () => {
    try {
      setIsExporting(true);
      setExportError(null);

      const response = await clinicService.exportClinicConfiguration(clinicId);

      // Create a blob from the data - using response directly instead of response.data
      const blob = new Blob([JSON.stringify(response, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      // Create and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `clinic_${clinicId}_config.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting clinic configuration:', error);
      setExportError('Failed to export clinic configuration. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportAnalyticsReport = async () => {
    try {
      setIsExportingReport(true);
      setReportError(null);

      const htmlContent = await analyticsService.exportClinicReportHtml(clinicId, reportDays, reportLocale);

      // Create a blob from the HTML content
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const url = URL.createObjectURL(blob);

      // Create and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `clinic_${clinicId}_analytics_report_${reportDays}days.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting analytics report:', error);
      setReportError('Failed to export analytics report. Please try again.');
    } finally {
      setIsExportingReport(false);
    }
  };

  return (
    <div className="bg-gray-50 p-6 rounded-lg">
      <h3 className="text-lg font-semibold text-gray-700 mb-6">診所配置與報告導出</h3>

      <ConfigJsonUpdatePanel clinicId={clinicId} onUpdated={onDataChange} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Configuration Export */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h4 className="text-md font-semibold mb-4 flex items-center">
            <DownloadIcon className="h-5 w-5 mr-2 text-gray-600" />
            配置導出
          </h4>
          <p className="text-gray-600 mb-4">
            導出當前診所配置為 JSON 文件。包括所有設定、
            醫生資訊、排程、知識庫條目和其他相關數據。
          </p>

          {exportError && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
              {exportError}
            </div>
          )}

          <button
            onClick={handleExport}
            disabled={isExporting}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300 flex items-center"
          >
            <DownloadIcon className="h-4 w-4 mr-2" />
            {isExporting ? '導出中...' : '導出診所配置'}
          </button>
        </div>

        {/* Analytics Report Export */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h4 className="text-md font-semibold mb-4 flex items-center">
            <DownloadIcon className="h-5 w-5 mr-2 text-gray-600" />
            分析報告導出
          </h4>
          <p className="text-gray-600 mb-4">
            導出診所分析報告為 HTML 格式。包括來電統計、
            預約轉換率、語音留言等詳細數據分析。
          </p>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              分析天數
            </label>
            <select
              value={reportDays}
              onChange={(e) => setReportDays(parseInt(e.target.value))}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={7}>最近 7 天</option>
              <option value={30}>最近 30 天</option>
              <option value={90}>最近 90 天</option>
              <option value={180}>最近 180 天</option>
              <option value={365}>最近 365 天</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              選擇要分析的時間範圍
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              語言 / Language
            </label>
            <select
              value={reportLocale}
              onChange={(e) => setReportLocale(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="zh-TW">繁體中文</option>
              <option value="en-US">English (US)</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              選擇報告語言
            </p>
          </div>

          {reportError && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
              {reportError}
            </div>
          )}

          <button
            onClick={handleExportAnalyticsReport}
            disabled={isExportingReport}
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-green-300 flex items-center"
          >
            <DownloadIcon className="h-4 w-4 mr-2" />
            {isExportingReport ? '生成中...' : `導出分析報告 (${reportDays}天)`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfigExportSection;
